/* eslint-disable react/no-array-index-key */
import { memo, useMemo, useState } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Box,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import { visuallyHidden } from "@mui/utils";

import SettingsIcon from "@mui/icons-material/Settings";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import CheckboxGroup from "components/SettingFields/atoms/CheckboxGroup";

import { cloneDeep } from "lodash";
import IconDisplay from "../../atoms/IconDisplay";
import ChipDisplay from "../../atoms/ChipDisplay";
import TextDisplay from "../../atoms/TextDisplay";
import BooleanSetting from "../../atoms/BooleanSetting";
import MultiBooleanSetting from "../../atoms/MultiBooleanSetting";
import InputSetting from "../../atoms/InputSetting";
import SingleSelectDropdown from "../../atoms/SingleSelectDropdown";
import MultiSelectDropdown from "../../atoms/MultiSelectDropdown";
import TableModal from "../TableModal";

const componentTypeMap = {
  boolean: {
    Component: BooleanSetting,
  },
  "multi-boolean": {
    Component: MultiBooleanSetting,
  },
  number: {
    Component: InputSetting,
  },
  "single-select-dropdown": {
    Component: SingleSelectDropdown,
  },
  "multi-select-dropdown": {
    Component: MultiSelectDropdown,
  },
  "text-display": {
    Component: TextDisplay,
  },
  icon: {
    Component: IconDisplay,
  },
  chip: {
    Component: ChipDisplay,
  },
  MDBox: {
    Component: MDBox,
  },
};

const ICONS = {
  setting: SettingsIcon,
  arrow_back: ArrowBackIcon,
};

const styles = {
  marginBottom: "0rem",
};

const columnWidths = ["30%", "20%", "20%", "30%"];

const SortableLabel = memo(
  ({ labelKey, children, order, orderBy, handleRequestSort }) => (
    <TableSortLabel
      active={orderBy === labelKey}
      direction={orderBy === labelKey ? order : "asc"}
      onClick={() => handleRequestSort(labelKey)}
    >
      {children}
      {orderBy === labelKey ? (
        <MDBox component="span" sx={visuallyHidden}>
          {order === "desc" ? "sorted descending" : "sorted ascending"}
        </MDBox>
      ) : null}
    </TableSortLabel>
  ),
  (prevProps, nextProps) =>
    prevProps.order === nextProps.order &&
    prevProps.orderBy === nextProps.orderBy &&
    prevProps.handleRequestSort === nextProps.handleRequestSort
);

const AppendEmptyTableCells = ({ row, columns }) => {
  const key = row[0].columnId === "orgAndAllTeams" ? "organizationTeamReportOn" : row[0].columnId;
  const matchedColumnFirstIndex = columns.findIndex((column) => column.columnId === key);
  return matchedColumnFirstIndex > 0
    ? Array(matchedColumnFirstIndex)
        .fill()
        .map(() => <TableCell />)
    : null;
};

function SettingsTable({ setting, setParentState }) {
  const theme = useTheme();
  const isMobileDisplay = useMediaQuery(theme.breakpoints.only("xs"));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalContextRow, setModalContextRow] = useState(null);
  const [duplicationCounts, setDuplicationCounts] = useState({});
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("percentage");
  const [searchFilter, setSearchFilter] = useState("");
  const [checkboxFilters, setCheckboxFilters] = useState(
    setting?.metadata?.checkBoxFilters?.checkboxes?.reduce((checkboxFilter, checkbox) => {
      checkboxFilter[checkbox.value] = true;
      return checkboxFilter;
    }, {}) || {}
  );

  function duplicateRow(rowIdx) {
    const copyTableSettingFieldValue = cloneDeep(setting.field_value);
    const allDuplicatedRowFields = copyTableSettingFieldValue.rows.slice(rowIdx).flat();

    // Find out the highest duplication group number to make sure new row has a unique group number
    const highestDuplicationGroupNum = allDuplicatedRowFields.reduce((maxGroupNum, field) => {
      if (field?.duplication && field?.duplication?.duplicationGroup > maxGroupNum) {
        return field?.duplication?.duplicationGroup;
      }
      return maxGroupNum;
    }, 0);

    // Copy the row and remove the toggle column
    const copyRow = cloneDeep(copyTableSettingFieldValue.rows[rowIdx]);
    const copyRowWithoutToggle = copyRow.filter(
      (rowColumn) => rowColumn?.duplication?.canDuplicate
    );

    // Update and clean up the fields before pushing into the table
    copyRowWithoutToggle.forEach((rowColumn) => {
      switch (rowColumn.columnId) {
        case "organizationTeamReportOn":
        case "orgAndAllTeams": {
          rowColumn.duplication.duplicationGroup += highestDuplicationGroupNum;
          rowColumn.duplication.duplicationBtnName = null;
          rowColumn.field_value = { value: "organization", display: "Entire Organization" };
          rowColumn.duplication.showTrashIcon = copyRowWithoutToggle.length === 1;
          break;
        }
        case "channelOrUser": {
          rowColumn.duplication.duplicationGroup += highestDuplicationGroupNum;
          rowColumn.field_value = { value: "channel", display: "Channel" };
          break;
        }
        case "recipientList": {
          rowColumn.duplication.duplicationGroup += highestDuplicationGroupNum;
          rowColumn.duplication.showTrashIcon = true;
          rowColumn.field_value = [];
          break;
        }
        default:
          break;
      }
    });

    // Make sure the new row is added right after the previous new row based on the same duplicate key
    const duplicationGroupKey = copyRowWithoutToggle[0].duplication.duplicateKey;
    const newDuplicationCount = duplicationCounts[duplicationGroupKey]
      ? duplicationCounts[duplicationGroupKey] + 1
      : 1;

    setDuplicationCounts((prev) => ({
      ...prev,
      [duplicationGroupKey]: newDuplicationCount,
    }));

    copyTableSettingFieldValue.rows.splice(rowIdx + newDuplicationCount, 0, copyRowWithoutToggle);
    setParentState(copyTableSettingFieldValue);
  }

  function deleteRow(rowIdx) {
    const copyTableSettingFieldValue = cloneDeep(setting.field_value);
    // Make sure to update the duplication count when a row is deleted, so it won't cause issues when adding a new row
    const duplicationGroupKey = copyTableSettingFieldValue.rows[rowIdx][0].duplication.duplicateKey;
    setDuplicationCounts((prev) => ({
      ...prev,
      [duplicationGroupKey]: prev[duplicationGroupKey] - 1,
    }));

    // Remove the specific row from the table
    copyTableSettingFieldValue.rows.splice(rowIdx, 1);
    setParentState(copyTableSettingFieldValue);
  }

  const AddOnFields = ({ fields }) =>
    fields.map((field) => {
      const ComponentToRender = componentTypeMap[field.settings_data_type].Component;
      return field?.addOnFields?.length ? (
        <ComponentToRender sx={field.styles}>
          <AddOnFields fields={field.addOnFields} />
        </ComponentToRender>
      ) : (
        <ComponentToRender sx={field.styles} setting={field} styles={field?.styles} />
      );
    });

  function descendingComparator(a, b) {
    const matchedColumn = setting.field_value.columns[orderBy];
    if (!matchedColumn) return 0;

    let valueA = a?.[orderBy]?.[matchedColumn.orderBy];
    let valueB = b?.[orderBy]?.[matchedColumn.orderBy];

    if (!valueA) return 1;
    if (!valueB) return -1;

    if (matchedColumn?.fieldType === "number") {
      valueA = valueA ? parseFloat(valueA) : 0;
      valueB = valueB ? parseFloat(valueB) : 0;
    } else {
      valueA = valueA?.toLowerCase();
      valueB = valueB?.toLowerCase();
    }
    if (valueB < valueA) {
      return -1;
    }
    if (valueB > valueA) {
      return 1;
    }
    return 0;
  }

  function getComparator() {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getFilteredData = (data) => {
    if (!setting?.metadata?.searchOn?.length && !Object.keys(checkboxFilters)?.length) return data;

    const filteredRows = [];
    data.forEach((row) => {
      if (Object.keys(checkboxFilters)?.length) {
        let shouldAllow = false;

        Object.entries(checkboxFilters).forEach(([key, value]) => {
          if (value) {
            const splitKey = key?.split("-");
            if (
              (splitKey?.[1] === "true" && row?.[0]?.[splitKey[0]]) ||
              (splitKey?.[1] === "false" && !row?.[0]?.[splitKey[0]])
            ) {
              shouldAllow = true;
            } else if (row?.[0]?.[splitKey[0]] === splitKey?.[1]) {
              shouldAllow = true;
            }
          }
        });

        if (!shouldAllow) return true;
      }

      if (setting?.metadata?.searchOn?.length) {
        setting.metadata.searchOn.forEach((searchOn) => {
          const matchedColumnIndex = setting.field_value.columns.findIndex(
            (column) => column?.columnId === searchOn
          );
          const regex = new RegExp(searchFilter, "i");
          if (regex.test(row[matchedColumnIndex]?.field_value)) {
            filteredRows.push(row);
          }
        });
      }
      return true;
    });

    return filteredRows;
  };

  const visibleRows = useMemo(() => {
    const rowsToFilter = setting.field_value.rows.map((row, index) => {
      row.originalIndex = index;
      return row;
    });
    const data = [
      ...(searchFilter || Object.keys(checkboxFilters)?.length
        ? getFilteredData(rowsToFilter)
        : rowsToFilter),
    ];
    return data.sort(getComparator());
  }, [order, orderBy, searchFilter, checkboxFilters, setting.field_value.rows]);

  return (
    <>
      {setting?.metadata?.title ? (
        <MDTypography variant="h4" mb={2}>
          {setting.metadata.title}
        </MDTypography>
      ) : null}
      {setting?.metadata?.description ? (
        <TextDisplay setting={{ field_value: setting.metadata.description }} />
      ) : null}
      {setting?.metadata?.addOnFields?.length ? (
        <AddOnFields fields={setting.metadata.addOnFields} />
      ) : null}
      <TableContainer
        component={Paper}
        sx={{
          marginBottom: "24px",
          ...(setting?.metadata?.styles?.tableContainer || {}),
        }}
      >
        <Box display="flex" my={2} flexDirection={isMobileDisplay ? "column" : "row"}>
          {setting?.metadata?.searchOn?.length ? (
            <MDInput
              label={setting?.metadata?.searchPlaceHolder || "Search Here"}
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              sx={{ mr: "1rem", width: "200px" }}
            />
          ) : null}
          {setting?.metadata?.checkBoxFilters ? (
            <CheckboxGroup
              isMobileDisplay={isMobileDisplay}
              checkboxes={setting?.metadata?.checkBoxFilters?.checkboxes}
              checkboxFilters={checkboxFilters}
              setCheckboxFilters={setCheckboxFilters}
            />
          ) : null}
        </Box>
        <Table
          sx={{
            minWidth: 650,
            [`& .${tableCellClasses.root}`]: {
              borderBottom: "0.5px solid #a9a9a9",
            },
            ...(setting?.metadata?.styles?.table || {}),
          }}
          aria-label="simple table"
        >
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              {setting.field_value.columns.map((column, index) => (
                <TableCell
                  key={`table-header-${column?.columnId}`}
                  style={{
                    width: columnWidths[index],
                    ...(setting?.metadata?.styles?.tableHeader?.tableCell?.[index] || {}),
                  }}
                >
                  {column?.sortable ? (
                    <SortableLabel
                      labelKey={index}
                      order={order}
                      orderBy={orderBy}
                      handleRequestSort={handleRequestSort}
                    >
                      {column.display}
                    </SortableLabel>
                  ) : (
                    column.display
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* loop through every single row */}
            {visibleRows.map((row) => {
              // If any one column contains duplicate functionality then need to align complete row at baseline to fix the center UI
              const shouldAlignRowAtBaseline = row.some(
                (rowColumnObject) =>
                  rowColumnObject?.duplication?.canDuplicate &&
                  rowColumnObject?.duplication?.duplicationBtnName
              );

              return (
                <TableRow
                  key={`table-row-wrapper-${row.originalIndex}`}
                  sx={{
                    "&:not(:last-child)": { borderBottom: "0.5px solid #a9a9a9" },
                  }}
                >
                  {/* If the row has only three columns, add an empty TableCell at the beginning */}
                  {row.length ? (
                    <AppendEmptyTableCells row={row} columns={setting.field_value.columns} />
                  ) : null}

                  {/* loop through every column of that row */}
                  {row.map((rowColumn, rowColumnIdx) => {
                    if (rowColumn.displayInTable === false) {
                      // don't render this column
                      return null;
                    }
                    const ComponentToRender =
                      componentTypeMap[rowColumn.settings_data_type].Component;
                    const IconToRender = ICONS[rowColumn?.icon?.name];

                    let options = rowColumn.options || [];

                    // For example, get value for recipient field based on the value of the channel field
                    if (rowColumn.optionsCalculatedFrom) {
                      const columnToCalculateFrom = rowColumn.optionsCalculatedFrom;

                      const foundRowColumnToComputeOptionsFrom = setting.field_value.rows[
                        row.originalIndex
                      ].find(
                        (rowColumnToSearch) => rowColumnToSearch.columnId === columnToCalculateFrom
                      );

                      const computedOptions =
                        setting.metadata.globalDropdownOptions[
                          foundRowColumnToComputeOptionsFrom?.field_value?.value
                        ];
                      options = computedOptions;
                    }

                    return (
                      <TableCell
                        key={`table-row-${row.originalIndex}-${rowColumn?.columnId}`}
                        sx={{
                          ...(shouldAlignRowAtBaseline ? { verticalAlign: "baseline" } : {}),
                        }}
                        colSpan={rowColumn?.mergeNextColumnsCount || 1}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            ...(rowColumn?.styles || {}),
                          }}
                        >
                          <ComponentToRender
                            setting={{ ...rowColumn, options }}
                            passWholeOption
                            setParentState={(newChildValue) => {
                              const copySettingFieldValue = cloneDeep(setting.field_value);
                              copySettingFieldValue.rows[row.originalIndex][
                                rowColumnIdx
                              ].field_value = newChildValue;
                              setParentState(copySettingFieldValue);

                              // When users switch between "channel" and "user", clear the recipient field
                              if (
                                newChildValue?.value === "channel" ||
                                newChildValue?.value === "user"
                              ) {
                                copySettingFieldValue.rows[row.originalIndex][
                                  rowColumnIdx + 1
                                ].field_value = [];
                              }
                            }}
                            styles={{
                              ...styles,
                              ...(rowColumn.settings_data_type === "icon"
                                ? setting?.metadata?.styles?.iconStyle
                                : {}),
                            }}
                          />
                          {rowColumn.settings_data_type === "boolean" && (
                            <Typography sx={{ fontSize: "15px" }}>
                              {rowColumn.field_label}
                            </Typography>
                          )}
                          {rowColumn?.icon && (
                            <Box sx={{ alignSelf: "flex-end" }}>
                              {rowColumn?.icon?.iconTooltip ? (
                                <Tooltip title={rowColumn.icon.iconTooltip}>
                                  <IconToRender
                                    style={rowColumn?.icon?.style || {}}
                                    onClick={() => {
                                      switch (rowColumn.icon.onClick) {
                                        case "openModal": {
                                          // Filter based on each modal's fieldIds, so that we only show the fields that are relevant to the modal
                                          let filterModalData = [];
                                          if (row[0]?.modalData) {
                                            filterModalData = row.filter(
                                              (theRowColumn) =>
                                                row[0].modalData.fieldIds[theRowColumn.columnId]
                                            );
                                          }
                                          setModalData(filterModalData);
                                          setModalContextRow(row.originalIndex);
                                          setIsModalOpen(true);
                                          break;
                                        }
                                        default:
                                          break;
                                      }
                                    }}
                                  />
                                </Tooltip>
                              ) : (
                                <IconToRender
                                  style={rowColumn?.icon?.style || {}}
                                  onClick={() => {
                                    switch (rowColumn.icon.onClick) {
                                      case "openModal": {
                                        // Filter based on each modal's fieldIds, so that we only show the fields that are relevant to the modal
                                        let filterModalData = [];
                                        if (row[0]?.modalData) {
                                          filterModalData = row.filter(
                                            (theRowColumn) =>
                                              row[0].modalData.fieldIds[theRowColumn.columnId]
                                          );
                                        }
                                        setModalData(filterModalData);
                                        setModalContextRow(row.originalIndex);
                                        setIsModalOpen(true);
                                        break;
                                      }
                                      default:
                                        break;
                                    }
                                  }}
                                />
                              )}
                            </Box>
                          )}
                          {rowColumn?.duplication?.showTrashIcon && (
                            <DeleteIcon
                              onClick={() => {
                                deleteRow(row.originalIndex);
                              }}
                              sx={{ ml: 2 }}
                            />
                          )}
                        </Box>
                        {rowColumn?.duplication?.duplicationBtnName && (
                          <Button
                            onClick={() => {
                              duplicateRow(row.originalIndex);
                            }}
                          >
                            {rowColumn?.duplication?.duplicationBtnName}
                          </Button>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {isModalOpen && modalData && (
        <TableModal
          open={isModalOpen}
          handleClose={() => {
            setIsModalOpen((prev) => !prev);
            setModalData(null);
          }}
          modalData={modalData}
          setParentState={(newModalData) => {
            const newModalDataColumnIds = new Set(newModalData.map((column) => column.columnId));
            // Make sure what users update in the modal is reflected in the table
            const copy = cloneDeep(setting.field_value);

            const existingSortIndex = {};
            copy.rows[modalContextRow].forEach((columnRow, index) => {
              if (!existingSortIndex[columnRow.columnId])
                existingSortIndex[columnRow.columnId] = columnRow.duplication
                  ? copy.rows[modalContextRow].length
                  : index;
            });

            // get column rows that weren't passed into the modal
            const rowWithoutModalColumns = copy.rows[modalContextRow].filter(
              (columnRow) => !newModalDataColumnIds.has(columnRow.columnId)
            );
            // "splice" in the new modal data
            rowWithoutModalColumns.push(...newModalData);
            // set the whole row with the newly calculated row
            copy.rows[modalContextRow] = rowWithoutModalColumns.sort(
              (a, b) => existingSortIndex[a.columnId] - existingSortIndex[b.columnId]
            );

            setModalData(null);
            setParentState(copy, true);
          }}
          setting={setting}
        />
      )}
    </>
  );
}

export default SettingsTable;
